import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';

// Cache client des sections de variantes (switch instantané) - 2026-08-30
const czVariantCache = new Map();
// 30/09/2026, audit vitesse, correctif A3. Le préchargement ne part plus au chargement
// (16 pages entières, 1,6 Mo, en concurrence avec l'image LCP) : il attend l'événement
// load puis un temps mort, ne demande que les variantes atteignables en UN clic depuis
// la sélection courante, une requête à la fois, et passe devant la file au survol ou au
// toucher d'une option. Le switch reste servi depuis le cache, donc instantané.
const czVariantPending = new Map();
const czPreloadQueue = [];
let czPreloadBusy = false;
let czPageLoaded = document.readyState === 'complete';
if (!czPageLoaded) window.addEventListener('load', () => { czPageLoaded = true; czPumpQueue(); }, { once: true });

function czFetchVariant(url) {
  if (czVariantCache.has(url)) return Promise.resolve(czVariantCache.get(url));
  if (czVariantPending.has(url)) return czVariantPending.get(url);
  const p = fetch(url)
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
    .then((t) => { czVariantCache.set(url, t); return t; })
    .finally(() => czVariantPending.delete(url));
  czVariantPending.set(url, p);
  return p;
}

function czPumpQueue() {
  if (czPreloadBusy || !czPageLoaded) return;
  let url;
  while ((url = czPreloadQueue.shift()) !== undefined) {
    if (!czVariantCache.has(url) && !czVariantPending.has(url)) break;
  }
  if (url === undefined) return;
  czPreloadBusy = true;
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 250));
  idle(() => {
    czFetchVariant(url).catch(() => {}).finally(() => { czPreloadBusy = false; czPumpQueue(); });
  }, { timeout: 3000 });
}
// import { morph } from '@theme/morph';

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;

  connectedCallback() {
    this.addEventListener('change', this.variantChanged.bind(this));
    this.#preloadVariants();
    // Survol ou toucher d'une option : on précharge cette variante-là en priorité.
    const onIntent = (e) => {
      if (!this.#preloadAllowed()) return;
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      let el = t.closest('input[data-option-value-id]');
      if (!el) {
        const lab = t.closest('label');
        if (lab) el = lab.htmlFor ? document.getElementById(lab.htmlFor) : lab.querySelector('input[data-option-value-id]');
      }
      if (el && !this.contains(el)) return;
      if (!el || !el.dataset.optionValueId || el.checked) return;
      const url = this.#urlForChange(el);
      if (url) czFetchVariant(url).catch(() => {});
    };
    this.addEventListener('pointerover', onIntent, { passive: true });
    this.addEventListener('touchstart', onIntent, { passive: true });
    this.addEventListener('focusin', onIntent);
  }

  // Préchargement autorisé : page produit, hors cartes et quick-add, hors économie de données.
  #preloadAllowed() {
    if (typeof Theme === 'undefined' || Theme.template?.name !== 'product') return false;
    if (this.closest('product-card') || this.closest('quick-add-dialog') || this.closest('quick-add-component')) return false;
    const conn = navigator.connection;
    if (conn && (conn.saveData || (conn.effectiveType || '').indexOf('2g') > -1)) return false;
    return !!this.dataset.productUrl;
  }

  // Groupes d'options : pour chaque fieldset ou select, la liste des éléments porteurs d'un id de valeur.
  #optionGroups() {
    const groups = [];
    for (const el of this.querySelectorAll('fieldset, select')) {
      const items = el.tagName === 'SELECT'
        ? Array.from(el.options).filter((o) => o.dataset.optionValueId)
        : Array.from(el.querySelectorAll('input[data-option-value-id]'));
      if (items.length) groups.push(items);
    }
    return groups;
  }

  // URL de la variante obtenue en changeant UNE option (celle de el), les autres restant sélectionnées.
  #urlForChange(el) {
    const groups = this.#optionGroups();
    const ids = [];
    for (const g of groups) {
      if (g.includes(el)) { ids.push(el.dataset.optionValueId); continue; }
      const sel = g.find((o) => (o instanceof HTMLOptionElement ? o.hasAttribute('selected') : o.checked));
      if (!sel) return null;
      ids.push(sel.dataset.optionValueId);
    }
    return `${this.dataset.productUrl}?option_values=${ids.join(',')}`;
  }

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Met à jour l'option sélectionnée dans l'interface
    this.updateSelectedOption(event.target);

    // Déclenche un événement personnalisé pour notifier la sélection
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // Lance la requête pour mettre à jour la section
    this.fetchUpdatedSection(this.buildRequestUrl());

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    this.updateUrl(event.target);
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    // Si c'est une chaîne, trouve l'élément correspondant pour récupérer l'ID de la valeur d'option
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Met à jour l'état "checked" pour les boutons radio
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    // Met à jour l'attribut "selected" pour les éléments select
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      // Supprime l'attribut selected de toutes les options
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      // Ajoute l'attribut selected à la nouvelle option
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl() {
    let productUrl = this.dataset.productUrl;

    // On utilise la nouvelle API de Shopify pour récupérer les données de variante avec option_values.
    const params = [];
    params.push(`option_values=${this.selectedOptionsValues.join(',')}`);

    // Si variant-picker est un enfant de quick-add-component ou swatches-variant-picker-component,
    // nous devons ajouter section_id=section-rendering-product-card à l'URL
    // if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
    //   if (productUrl?.includes('?')) {
    //     productUrl = productUrl.split('?')[0];
    //   }
    //   return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    // }
    return `${productUrl}?${params.join('&')}`;
  }

  // Récupère la section mise à jour : cache client d'abord (instantané), sinon réseau.
  fetchUpdatedSection(requestUrl) {
    const cached = czVariantCache.get(requestUrl);
    if (cached) {
      this.#applySection(cached);
      return;
    }

    // Annule la requête fetch précédente si elle est encore en cours
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Préchargement déjà en vol pour cette variante : on l'attend plutôt que de la redemander.
    const pending = czVariantPending.get(requestUrl);
    if (pending) {
      const ctrl = this.#abortController;
      pending.then((t) => { if (!ctrl.signal.aborted) this.#applySection(t); }).catch(() => {
        if (!ctrl.signal.aborted) fetch(requestUrl, { signal: ctrl.signal }).then((r) => r.text()).then((t) => { czVariantCache.set(requestUrl, t); this.#applySection(t); }).catch(() => {});
      });
      return;
    }

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        czVariantCache.set(requestUrl, responseText);
        this.#applySection(responseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log("Fetch interrompu par l'utilisateur");
        } else {
          console.error(error);
        }
      });
  }

  // Applique une réponse de section (réseau ou cache) : identique au flux d'origine.
  #applySection(responseText) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');

    const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
    if (!textContent) return;

    this.updateVariantPicker(html);

    // Nouvelle sélection : on remet en file ses propres voisines.
    this.#preloadVariants();

    if (this.selectedOptionId) {
      this.dispatchEvent(
        new VariantUpdatedEvent(JSON.parse(textContent), this.selectedOptionId, {
          html,
          productId: this.dataset.productId ?? '',
        }),
      );
    }
  }

  // Met en file, après load et un temps mort, les variantes atteignables en un clic
  // depuis la sélection courante (voisines). Rappelée après chaque changement de variante.
  #preloadVariants() {
    try {
      if (!this.#preloadAllowed()) return;
      const groups = this.#optionGroups();
      if (!groups.length) return;
      let n = 0;
      for (const g of groups) {
        for (const el of g) {
          const isSel = el instanceof HTMLOptionElement ? el.hasAttribute('selected') : el.checked;
          if (isSel) continue;
          const url = this.#urlForChange(el);
          if (url && !czVariantCache.has(url) && !czPreloadQueue.includes(url)) czPreloadQueue.push(url);
          if (++n >= 30) break;
        }
      }
      czPumpQueue();
    } catch (e) {
      /* préchargement best-effort */
    }
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    // Trouve le nouveau variant picker dans la réponse
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }

    // Met à jour le variant picker
    this.innerHTML = newVariantPickerSource.innerHTML;
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(target) {
    // Détermine si nous sommes sur une page produit (pas dans une carte produit ou dialogue)
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Prépare l'URL pour la mise à jour de l'historique du navigateur
    const url = new URL(window.location.href);

    let variantId;

    // Extrait l'ID de la variante selon le type d'élément (radio ou select)
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      variantId = target.dataset.variantId || null;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Met à jour l'URL dans l'historique du navigateur si elle a changé
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }

    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("Aucun ID de valeur d'option trouvé");

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
